import {
  HERO_FONT_OPTIONS,
  HERO_SHADOW_OPTIONS,
  HERO_SHADOW_COLOR_PRESETS,
  TEXT_WEIGHT_OPTIONS,
  getHeroFontFamily,
  getHeroTextShadow,
  getTextWeightClass,
  type HeroTextFont,
  type HeroTextShadow,
  type TextWeight,
} from '@/services/heroText';

// Valor de estilo de texto reutilizable (portada y títulos de sección).
export interface TextStyleValue {
  color: string;
  shadow: HeroTextShadow;
  shadowColor: string;
  font: HeroTextFont;
  size: string;
  weight: TextWeight;
}

interface SizeOption {
  value: string;
  label: string;
}

interface ColorPreset {
  value: string;
  label: string;
}

interface TextStyleControlsProps {
  value: TextStyleValue;
  onChange: (patch: Partial<TextStyleValue>) => void;
  sizeOptions: SizeOption[];
  resolveSizeClass: (value: string) => string;
  colorPresets: ColorPreset[];
  previewTitle?: string;
  previewSubtitle?: string;
  previewClassName?: string;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-foreground-600 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

// Panel reutilizable de estilo del texto: color, sombra, tipografía, tamaño y
// grosor, con una vista previa en vivo. Se usa tanto para la portada como para
// los títulos de sección (Referencias y Propósito).
export default function TextStyleControls({
  value,
  onChange,
  sizeOptions,
  resolveSizeClass,
  colorPresets,
  previewTitle,
  previewSubtitle,
  previewClassName = 'bg-gradient-to-br from-background-200 to-foreground-300',
}: TextStyleControlsProps) {
  return (
    <div className="space-y-4">
      <Field label="Color de la letra">
        <div className="flex flex-wrap items-center gap-2">
          {colorPresets.map((c) => {
            const active = value.color.toUpperCase() === c.value.toUpperCase();
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => onChange({ color: c.value })}
                className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  active
                    ? 'border-accent-500 bg-accent-50 text-accent-700'
                    : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
                }`}
              >
                <span
                  className="w-4 h-4 rounded-full border border-black/10"
                  style={{ backgroundColor: c.value }}
                />
                {c.label}
              </button>
            );
          })}
          <label className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border border-background-300 bg-white cursor-pointer text-xs font-medium text-foreground-600 hover:border-background-400 whitespace-nowrap">
            <input
              type="color"
              value={value.color}
              onChange={(e) => onChange({ color: e.target.value.toUpperCase() })}
              className="w-5 h-5 rounded cursor-pointer border-0 bg-transparent p-0"
              aria-label="Color personalizado"
            />
            Personalizado
          </label>
        </div>
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Tamaño del título">
          <div className="flex flex-wrap gap-2">
            {sizeOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => onChange({ size: o.value })}
                className={`px-4 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  value.size === o.value
                    ? 'border-accent-500 bg-accent-50 text-accent-700'
                    : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Grosor de la letra">
          <div className="flex flex-wrap gap-2">
            {TEXT_WEIGHT_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => onChange({ weight: o.value })}
                style={{ fontWeight: o.value === 'normal' ? 400 : o.value === 'semibold' ? 600 : o.value === 'bold' ? 700 : o.value === 'extrabold' ? 800 : 900 }}
                className={`px-4 py-2 rounded-lg border text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  value.weight === o.value
                    ? 'border-accent-500 bg-accent-50 text-accent-700'
                    : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>
      </div>

      <Field label="Sombra del texto (para resaltar sobre el fondo)">
        <div className="flex flex-wrap gap-2">
          {HERO_SHADOW_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange({ shadow: o.value })}
              className={`px-4 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                value.shadow === o.value
                  ? 'border-accent-500 bg-accent-50 text-accent-700'
                  : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </Field>

      {value.shadow !== 'none' && (
        <Field label="Color de la sombra (neón para que brille)">
          <div className="flex flex-wrap items-center gap-2">
            {HERO_SHADOW_COLOR_PRESETS.map((c) => {
              const active = value.shadowColor.toUpperCase() === c.value.toUpperCase();
              const isLight = c.value.toUpperCase() === '#FFFFFF' || c.value.toUpperCase() === '#FFE600';
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => onChange({ shadowColor: c.value })}
                  className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? 'border-accent-500 bg-accent-50 text-accent-700'
                      : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full border ${isLight ? 'border-black/20' : 'border-black/10'}`}
                    style={{ backgroundColor: c.value, boxShadow: `0 0 6px ${c.value}` }}
                  />
                  {c.label}
                </button>
              );
            })}
            <label className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border border-background-300 bg-white cursor-pointer text-xs font-medium text-foreground-600 hover:border-background-400 whitespace-nowrap">
              <input
                type="color"
                value={value.shadowColor}
                onChange={(e) => onChange({ shadowColor: e.target.value.toUpperCase() })}
                className="w-5 h-5 rounded cursor-pointer border-0 bg-transparent p-0"
                aria-label="Color de sombra personalizado"
              />
              Personalizado
            </label>
          </div>
          <p className="text-xs text-foreground-400 mt-1.5">
            El negro clásico resalta; los tonos neón crean un halo de color alrededor de la letra.
          </p>
        </Field>
      )}

      <Field label="Tipo de letra">
        <div className="flex flex-wrap gap-2">
          {HERO_FONT_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange({ font: o.value })}
              style={{ fontFamily: o.fontFamily }}
              className={`px-4 py-2 rounded-lg border text-sm transition-colors cursor-pointer whitespace-nowrap ${
                value.font === o.value
                  ? 'border-accent-500 bg-accent-50 text-accent-700'
                  : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </Field>

      <div className={`rounded-lg px-4 py-5 ${previewClassName}`}>
        <p className="text-[10px] uppercase tracking-wider text-foreground-600 text-center mb-2">
          Vista previa
        </p>
        <p
          className={`${resolveSizeClass(value.size)} ${getTextWeightClass(value.weight)} text-center leading-tight`}
          style={{
            color: value.color,
            textShadow: getHeroTextShadow(value.shadow, value.shadowColor),
            fontFamily: getHeroFontFamily(value.font),
          }}
        >
          {previewTitle || 'Título de ejemplo'}
        </p>
        {previewSubtitle ? (
          <p
            className="text-sm text-center mt-1.5 opacity-90"
            style={{
              color: value.color,
              textShadow: getHeroTextShadow(value.shadow, value.shadowColor),
              fontFamily: getHeroFontFamily(value.font),
            }}
          >
            {previewSubtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
}